import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { USER_ROLES } from '../data/index.js'
import { homeworkApi, userApi, favoriteApi, learnApi } from '../services/api.js'

const AppContext = createContext(null)

// 首页页面ID列表（与 pageMetadata 中 isMain:true 保持一致）
const MAIN_PAGES = ['learn', 'ai', 'courses', 'profile', 'home', 'cart']

export function AppProvider({ children }) {
  const [page, setPage] = useState('learn')
  const [history, setHistory] = useState([{ page: 'learn', title: '学习中心' }])
  const [currentCourseId, setCurrentCourseId] = useState(null)
  const [currentKnowledge, setCurrentKnowledge] = useState(null)
  const [pageState, setPageState] = useState(null)
  const [toast, setToast] = useState({ msg: '', show: false })
  const [favorites, setFavorites] = useState(() => new Set())
  const [darkMode, setDarkMode] = useState(false)
  const [checkedIn, setCheckedIn] = useState(false)
  const [exitConfirm, setExitConfirm] = useState(false)
  const [myCourses, setMyCourses] = useState([])
  
  const [user, setUser] = useState(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  
  // 身份系统状态
  const [userRole, setUserRole] = useState(() => {
    const saved = localStorage.getItem('edu_user_role')
    return saved || USER_ROLES.STUDENT
  })
  
  // 作业系统状态
  const [homeworks, setHomeworks] = useState([])
  const [homeworkLoading, setHomeworkLoading] = useState(false)
  
  // 当前编辑的作业ID
  const [currentHomeworkId, setCurrentHomeworkId] = useState(null)

  const toastTimerRef = useRef(null)
  const backPressTimerRef = useRef(null)
  const isProcessingBack = useRef(false) // 防止重复处理
  const exitConfirmRef = useRef(exitConfirm)
  exitConfirmRef.current = exitConfirm
  const pageRef = useRef(page)
  pageRef.current = page
  
  // 页面元数据配置
  const pageMetadata = {
    learn: { title: '学习中心', showBack: false, isMain: true },
    courses: { title: '课程中心', showBack: false, isMain: true },
    ai: { title: 'AI助手', showBack: false, isMain: true },
    home: { title: '首页', showBack: false, isMain: true },
    profile: { title: '个人中心', showBack: false, isMain: true },
    detail: { title: '课程详情', showBack: true, isMain: false },
    search: { title: '搜索', showBack: true, isMain: false },
    settings: { title: '设置', showBack: true, isMain: false },
    login: { title: '登录', showBack: true, isMain: false },
    video: { title: '视频学习', showBack: true, isMain: false },
    cart: { title: '切换功能', showBack: false, isMain: true },
    knowledge: { title: '详情', showBack: false, isMain: false },
    homework: { title: '作业详情', showBack: true, isMain: false },
    homeworkList: { title: '作业列表', showBack: true, isMain: false },
    homeworkCreate: { title: '发布作业', showBack: true, isMain: false },
    homeworkHistory: { title: '历史作业', showBack: true, isMain: false },
    mistakeBook: { title: '错题本', showBack: true, isMain: false },
    learnTrack: { title: '学习轨迹', showBack: true, isMain: false },
    aiRecommend: { title: 'AI 学习推荐', showBack: true, isMain: false },
    identity: { title: '选择身份', showBack: true, isMain: false },
    privacy: { title: '隐私政策', showBack: true, isMain: false },
    terms: { title: '用户协议', showBack: true, isMain: false },
  }
  
  // 保持向后兼容
  const currentPageMeta = pageMetadata[page] || { title: '', showBack: false, isMain: true }
  
  // 是否在首页
  const isOnMainPage = MAIN_PAGES.includes(page)
  
  // Toast 函数
  const showToast = useCallback((msg) => {
    setToast({ msg, show: true })
    clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => setToast(t => ({ ...t, show: false })), 2200)
  }, [])

  // 返回到首页
  const goToHome = useCallback(() => {
    setPage('learn')
    setHistory([{ page: 'learn', title: '学习中心' }])
    showToast('已返回')
  }, [showToast])

  // 页面导航函数 — 必须在 useEffect 之前定义，避免 TDZ 错误
  const navigate = useCallback((p, courseId = null, knowledge = null, title = null, state = null) => {
    if (courseId) setCurrentCourseId(courseId)
    if (knowledge !== null) setCurrentKnowledge(knowledge)
    setPageState(state)
    
    const pageTitle = title || pageMetadata[p]?.title || p
    
    setHistory(h => {
      if (MAIN_PAGES.includes(p)) {
        return [{ page: p, title: pageTitle }]
      }
      return [...h, { page: p, title: pageTitle }]
    })
    setPage(p)
  }, [pageMetadata])

  const goBack = useCallback(() => {
    setHistory(h => {
      if (h.length <= 1) {
        setPage('learn')
        return [{ page: 'learn', title: '学习中心' }]
      }
      const next = [...h]
      next.pop()
      const targetPage = next[next.length - 1]
      setPage(targetPage.page)
      return next
    })
  }, [])

  const goBackTo = useCallback((targetPage) => {
    setHistory(h => {
      const index = h.findIndex(item => item.page === targetPage)
      if (index !== -1) {
        const next = h.slice(0, index + 1)
        setPage(targetPage)
        return next
      }
      setPage('learn')
      return [{ page: 'learn', title: '学习中心' }]
    })
  }, [])

  // 返回键处理函数 - 使用 ref 避免依赖变化导致循环
  const handleBackPress = useCallback(() => {
    // 防止重复处理
    if (isProcessingBack.current) return
    isProcessingBack.current = true
    setTimeout(() => { isProcessingBack.current = false }, 300)
    
    const currentExitConfirm = exitConfirmRef.current
    const currentPage = pageRef.current
    
    // 如果正在显示退出确认对话框，关闭它
    if (currentExitConfirm) {
      setExitConfirm(false)
      if (backPressTimerRef.current) clearTimeout(backPressTimerRef.current)
      return
    }
    
    // 如果不在首页页面，返回上一页
    if (!MAIN_PAGES.includes(currentPage)) {
      goBack()
      return
    }
    
    // 在首页，显示退出确认
    setExitConfirm(true)
    showToast('再按一次返回键退出应用')
    if (backPressTimerRef.current) clearTimeout(backPressTimerRef.current)
    backPressTimerRef.current = setTimeout(() => setExitConfirm(false), 3000)
  }, [])

  // 监听 401 登录过期事件（用 React 导航，避免整页刷新）
  useEffect(() => {
    const handleAuthExpired = () => {
      // 如果本来就没登录（token不存在），忽略 401 事件，不跳转
      const hadToken = !!localStorage.getItem('edu_token')
      setUser(null)
      setIsLoggedIn(false)
      setFavorites(new Set())
      // 清理残留的无效 token
      localStorage.removeItem('edu_token')
      localStorage.removeItem('edu_user')
      // 只有之前确实登录过的用户才提示"过期"并跳转登录页
      if (hadToken) {
        showToast('登录已过期，请重新登录')
        navigate('login')
      }
    }
    window.addEventListener('auth:expired', handleAuthExpired)
    return () => window.removeEventListener('auth:expired', handleAuthExpired)
  }, [showToast, navigate])

  // 监听Android物理返回键（仅在 Capacitor APP 环境中）
  useEffect(() => {
    const isApp = typeof window.Capacitor !== 'undefined'
    
    const handleCapacitorBack = () => {
      console.log('Capacitor backbutton event received')
      handleBackPress()
    }
    
    const handleAndroidBack = () => {
      console.log('Android backbutton event received')
      handleBackPress()
    }

    // 仅在 APP 环境中绑定事件，避免浏览器中 popstate 导致循环刷新
    if (isApp) {
      window.addEventListener('backbutton', handleCapacitorBack)
      window.addEventListener('androidbackbutton', handleAndroidBack)
      window.addEventListener('popstate', handleBackPress)
    }
    
    return () => {
      if (isApp) {
        window.removeEventListener('backbutton', handleCapacitorBack)
        window.removeEventListener('androidbackbutton', handleAndroidBack)
        window.removeEventListener('popstate', handleBackPress)
      }
      if (backPressTimerRef.current) {
        clearTimeout(backPressTimerRef.current)
      }
    }
  }, [handleBackPress])
  
  // 用户数据加载
  useEffect(() => {
    const savedUser = localStorage.getItem('edu_user')
    if (savedUser) {
      try {
        const userInfo = JSON.parse(savedUser)
        setUser(userInfo)
        setIsLoggedIn(true)
      } catch (e) {
        localStorage.removeItem('edu_user')
      }
    }
  }, [])
  
  // 从服务器获取收藏列表
  const fetchFavorites = useCallback(async () => {
    const token = localStorage.getItem('edu_token')
    if (!token) return
    try {
      const res = await favoriteApi.getList()
      if (res.list) {
        const ids = new Set(res.list.map(item => item.courseId))
        setFavorites(ids)
      }
    } catch (err) {
      console.error('获取收藏列表失败:', err)
    }
  }, [])

  // 从服务器获取我的课程（有进度的）
  const fetchMyCourses = useCallback(async () => {
    const token = localStorage.getItem('edu_token')
    if (!token) return
    try {
      const res = await learnApi.getMyCourses()
      if (res.list) {
        setMyCourses(res.list)
      }
    } catch (err) {
      console.error('获取我的课程失败:', err)
    }
  }, [])

  // 从服务器获取最新用户资料
  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem('edu_token')
    if (!token) return
    try {
      const res = await userApi.getProfile()
      if (res.user) {
        setUser(res.user)
        setIsLoggedIn(true)
        localStorage.setItem('edu_user', JSON.stringify(res.user))
        // 登录后同步收藏列表和课程进度
        fetchFavorites()
        fetchMyCourses()
      }
    } catch (err) {
      console.error('获取用户资料失败:', err)
    }
  }, [fetchFavorites, fetchMyCourses])
  
  const logout = useCallback(() => {
    localStorage.removeItem('edu_user')
    setUser(null)
    setIsLoggedIn(false)
    setFavorites(new Set())
    showToast('已退出登录')
    navigate('learn')
  }, [showToast, navigate])

  const clearExitConfirm = useCallback(() => {
    setExitConfirm(false)
    if (backPressTimerRef.current) {
      clearTimeout(backPressTimerRef.current)
    }
  }, [])

  const toggleFav = useCallback(async (id) => {
    const isFav = favorites.has(id)
    try {
      if (isFav) {
        await favoriteApi.remove(id)
      } else {
        await favoriteApi.add(id)
      }
      setFavorites(f => {
        const next = new Set(f)
        if (next.has(id)) { next.delete(id); showToast('已取消收藏') }
        else { next.add(id); showToast('已收藏') }
        return next
      })
    } catch (err) {
      showToast(err.message || '操作失败')
    }
  }, [favorites, showToast])

  // 身份切换
  const changeRole = useCallback((role) => {
    setUserRole(role)
    localStorage.setItem('edu_user_role', role)
    showToast(`已切换为${role === USER_ROLES.TEACHER ? '老师' : role === USER_ROLES.STUDENT ? '学生' : '自由学习者'}身份`)
  }, [showToast])

  // 加载作业列表（未登录时静默跳过，不触发 401 跳转）
  const fetchHomeworks = useCallback(async (params = {}) => {
    const token = localStorage.getItem('edu_token')
    if (!token) {
      // 未登录时直接返回空数据，不调用需要认证的 API
      setHomeworks([])
      return
    }
    setHomeworkLoading(true)
    try {
      const res = await homeworkApi.getList(params)
      setHomeworks(res.list || [])
    } catch (err) {
      console.error('加载作业失败:', err)
      // 401 错误由全局拦截器处理，其他错误静默
      if (err.message && !err.message.includes('过期')) {
        setHomeworks([])
      }
    } finally {
      setHomeworkLoading(false)
    }
  }, [])

  // 提交作业
  const submitHomework = useCallback(async (homeworkId, submission) => {
    try {
      await homeworkApi.submit(homeworkId, submission)
      showToast('作业提交成功！')
      // 重新加载作业列表
      await fetchHomeworks()
    } catch (err) {
      showToast(err.message || '提交失败')
    }
  }, [showToast, fetchHomeworks])

  // 批改作业
  const gradeHomework = useCallback(async (homeworkId, submissionId, grade, comment) => {
    try {
      await homeworkApi.grade(homeworkId, submissionId, { grade, comment })
      showToast('批改完成！')
      await fetchHomeworks()
    } catch (err) {
      showToast(err.message || '批改失败')
    }
  }, [showToast, fetchHomeworks])

  // 创建作业（老师）
  const createHomework = useCallback(async (homework) => {
    try {
      await homeworkApi.create(homework)
      showToast('作业发布成功！')
      await fetchHomeworks()
    } catch (err) {
      showToast(err.message || '发布失败')
    }
  }, [showToast, fetchHomeworks])

  // 删除作业（老师）
  const deleteHomework = useCallback(async (homeworkId) => {
    try {
      await homeworkApi.remove(homeworkId)
      showToast('作业已删除')
      await fetchHomeworks()
    } catch (err) {
      showToast(err.message || '删除失败')
    }
  }, [showToast, fetchHomeworks])

    return (
    <AppContext.Provider value={{
      page, navigate, goBack, goBackTo,
      currentPageMeta,
      currentCourseId, setCurrentCourseId,
      currentKnowledge, setCurrentKnowledge, pageState, setPageState,
      toast, showToast,
      favorites, toggleFav, fetchFavorites,
      darkMode, setDarkMode,
      checkedIn, setCheckedIn,
      user, setUser, isLoggedIn, setIsLoggedIn, logout, fetchProfile,
      myCourses, fetchMyCourses,
      exitConfirm, setExitConfirm: clearExitConfirm,
      // 身份系统
      userRole, changeRole,
      USER_ROLES,
      // 作业系统
      homeworks, setHomeworks, homeworkLoading, fetchHomeworks,
      currentHomeworkId, setCurrentHomeworkId,
      submitHomework,
      gradeHomework,
      createHomework,
      deleteHomework,
    }}>
      {children}
    </AppContext.Provider>
  )
}

export const useApp = () => useContext(AppContext)
